import { cn } from "@/utils/ui";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Link } from "@tanstack/react-router";
import { useRafInterval } from "ahooks";
import { motion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";

interface NotFoundProps {
  title?: string;
}

// Pixel art colors - NES palette inspired
const PIXEL_COLORS = [
  "#0d2b45",
  "#203c56",
  "#544e68",
  "#8d697a",
  "#d08159",
  "#ffaa5e",
  "#ffd4a3",
  "#ffecd6",
];

const SHAPES = ["line", "square", "cross", "diagonal"];

export function NotFound({
  title = "404: 虚空画廊第███号展厅遭遇量子扰动",
}: NotFoundProps) {
  const [pixelGrid, setPixelGrid] = useState<
    Array<{ color: string; shape: string }>
  >([]);
  const [flashingPoints, setFlashingPoints] = useState<number[]>([]);
  const [clickCount, setClickCount] = useState(0);
  const [floatingPixel, setFloatingPixel] = useState<number | null>(null);
  const [gridOffset, setGridOffset] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  // Initialize pixel grid and flashing points
  useEffect(() => {
    // Create empty 8x8 grid (64 pixels) instead of 16x16 (256 pixels)
    const initialGrid = Array(64)
      .fill(null)
      .map(() => ({
        color: PIXEL_COLORS[Math.floor(Math.random() * 3)],
        shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
      }));
    setPixelGrid(initialGrid);

    // Set 3 random flashing points
    setFlashingPoints([
      Math.floor(Math.random() * 64),
      Math.floor(Math.random() * 64),
      Math.floor(Math.random() * 64),
    ]);

    // Initialize audio context
    try {
      audioContextRef.current = new window.AudioContext();
    } catch (_e) {
      console.error("Web Audio API not supported");
    }

    // Set floating pixel every 20 seconds
    const floatingInterval = setInterval(() => {
      setFloatingPixel(Math.floor(Math.random() * 64));
      setTimeout(() => setFloatingPixel(null), 5000);
    }, 20000);

    return () => clearInterval(floatingInterval);
  }, []);

  // Play 8-bit sound effect
  const playSound = useCallback(() => {
    if (!audioContextRef.current) {
      return;
    }

    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();

    oscillator.type = "square";
    oscillator.frequency.setValueAtTime(
      500,
      audioContextRef.current.currentTime,
    );
    gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(
      0.01,
      audioContextRef.current.currentTime + 0.1,
    );

    oscillator.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);

    oscillator.start();
    oscillator.stop(audioContextRef.current.currentTime + 0.1);
  }, []);

  // Handle pixel click
  const handlePixelClick = useCallback(
    (index: number) => {
      if (!flashingPoints.includes(index)) {
        return;
      }

      // Generate new pixel art at the clicked location
      const newGrid = [...pixelGrid];
      const colors = [
        PIXEL_COLORS[Math.floor(Math.random() * PIXEL_COLORS.length)],
        PIXEL_COLORS[Math.floor(Math.random() * PIXEL_COLORS.length)],
        PIXEL_COLORS[Math.floor(Math.random() * PIXEL_COLORS.length)],
      ];

      newGrid[index] = {
        color: colors[0],
        shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
      };

      // Update surrounding pixels to create a pattern - adjusted for 8x8 grid
      const row = Math.floor(index / 8);
      const col = index % 8;

      for (let r = -1; r <= 1; r++) {
        for (let c = -1; c <= 1; c++) {
          const newRow = row + r;
          const newCol = col + c;
          if (newRow >= 0 && newRow < 8 && newCol >= 0 && newCol < 8) {
            const newIndex = newRow * 8 + newCol;
            if (Math.random() > 0.5) {
              newGrid[newIndex] = {
                color: colors[Math.floor(Math.random() * colors.length)],
                shape: SHAPES[Math.floor(Math.random() * SHAPES.length)],
              };
            }
          }
        }
      }

      setPixelGrid(newGrid);
      playSound();

      // Generate new flashing point
      const newFlashingPoints = [...flashingPoints];
      const pointIndex = newFlashingPoints.indexOf(index);
      newFlashingPoints[pointIndex] = Math.floor(Math.random() * 64);
      setFlashingPoints(newFlashingPoints);

      // Update counter and check for achievement
      const newClickCount = clickCount + 1;
      setClickCount(newClickCount);

      if (newClickCount % 5 === 0) {
        NiceModal.show(YouAreBoringPopup, {
          showNothingText: newClickCount > 5,
        });
      }
    },
    [flashingPoints, pixelGrid, clickCount, playSound],
  );

  // Randomly change grid offset every 500ms simulating a sway
  useRafInterval(() => {
    setGridOffset({
      x: (Math.random() - 0.5) * 100,
      y: (Math.random() - 0.5) * 10,
    });
  }, 1000);

  return (
    <motion.div
      ref={containerRef}
      className="relative flex flex-col items-center size-full text-[#ffecd6] font-pixel p-6 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{
        imageRendering: "pixelated",
      }}
    >
      {/* Dynamic Display Area */}
      <div className="flex flex-col items-center justify-center relative">
        <h1
          className="text-xl mb-2 glitch-text"
          style={{
            textShadow: "1px 1px 0 #d08159, -1px -1px 0 #544e68",
            letterSpacing: "1px",
          }}
          data-text={title}
        >
          {title}
        </h1>

        <div className="px-6 py-3 mb-6 text-center border border-dashed border-[#544e68] bg-[#0d2b45]/40 backdrop-blur-sm rounded-sm">
          <p className="text-sm mb-1 opacity-80">
            "您刚刚触发的是上世纪最伟大的数字艺术事故"
          </p>
          <p className="text-xs opacity-70 italic text-[#ffaa5e] tracking-wider">
            ──《比特》杂志 1998年<span className="font-bold">█</span>月
            <span className="font-bold">█</span>号评虚空画廊事件
          </p>
        </div>
      </div>

      {/* Interaction Area */}
      <motion.div
        className="w-full max-w-[256px] flex items-center justify-center"
        animate={{
          x: gridOffset.x,
          y: gridOffset.y,
        }}
        transition={{
          type: "spring",
          stiffness: 100,
          damping: 10,
          duration: 0.2,
        }}
      >
        <div className="grid grid-cols-8 gap-0 w-full aspect-square border-5 border-graya-border-subtle">
          {pixelGrid.map((pixel, index) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: This is a static grid
              key={index}
              className={cn(
                "aspect-square cursor-pointer",
                flashingPoints.includes(index) && "animate-flicker",
                index === floatingPixel && "floating-pixel",
              )}
              style={{
                backgroundColor: pixel.color,
                boxShadow: flashingPoints.includes(index)
                  ? "0 0 8px #ffaa5e"
                  : "none",
                transform:
                  index === floatingPixel ? "translateY(-8px)" : "none",
                transition: "transform 2s ease-in-out",
              }}
              onClick={() => handlePixelClick(index)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handlePixelClick(index);
                }
              }}
            />
          ))}
        </div>
      </motion.div>

      {/* Control Bar */}
      <div className="p-6 flex gap-3 items-center">
        <div className="text-sm mr-2">
          量子稳定度：
          <span className="text-[#ffaa5e]">
            {(clickCount * 13.7).toFixed(1)}%
          </span>
        </div>

        <button
          type="button"
          onClick={() => window.history.back()}
          className="px-3 py-2 bg-[#203c56] text-[#ffd4a3] border border-[#544e68] hover:bg-[#544e68] transition-colors shadow-glitch"
        >
          ← 事件视界
        </button>

        <Link
          to="/"
          className="px-3 py-2 bg-[#203c56] text-[#ffd4a3] border border-[#544e68] hover:bg-[#544e68] transition-colors shadow-glitch"
        >
          ■ 奇点重启
        </Link>

        <button
          type="button"
          className="px-3 py-2 bg-[#203c56] text-[#ffd4a3] border border-[#544e68] hover:bg-[#544e68] transition-colors shadow-glitch"
        >
          ▲ 上报管理局
        </button>
      </div>

      <style>{`
        @keyframes flicker {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }

        .animate-flicker {
          animation: flicker 0.2s step-end infinite;
        }

        .shadow-glitch {
          box-shadow: 2px 2px 0 #d08159, -2px -2px 0 #544e68;
        }

        .glitch-text {
          position: relative;
          display: inline-block;
        }

        .glitch-text::before, .glitch-text::after {
          content: attr(data-text);
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        .glitch-text::before {
          left: 1px;
          text-shadow: -1px 0 #d08159;
          clip: rect(44px, 450px, 56px, 0);
          animation: glitch-anim 5s infinite linear alternate-reverse;
        }

        .glitch-text::after {
          left: -1px;
          text-shadow: -1px 0 #544e68;
          clip: rect(44px, 450px, 56px, 0);
          animation: glitch-anim2 5s infinite linear alternate-reverse;
        }

        @keyframes glitch-anim {
          0% { clip: rect(16px, 9999px, 86px, 0); }
          20% { clip: rect(25px, 9999px, 22px, 0); }
          40% { clip: rect(48px, 9999px, 7px, 0); }
          60% { clip: rect(74px, 9999px, 92px, 0); }
          80% { clip: rect(38px, 9999px, 41px, 0); }
          100% { clip: rect(53px, 9999px, 18px, 0); }
        }

        @keyframes glitch-anim2 {
          0% { clip: rect(65px, 9999px, 34px, 0); }
          20% { clip: rect(17px, 9999px, 48px, 0); }
          40% { clip: rect(9px, 9999px, 55px, 0); }
          60% { clip: rect(81px, 9999px, 72px, 0); }
          80% { clip: rect(46px, 9999px, 33px, 0); }
          100% { clip: rect(28px, 9999px, 96px, 0); }
        }
      `}</style>
    </motion.div>
  );
}

const YouAreBoringPopup = NiceModal.create(
  ({ showNothingText }: { showNothingText: boolean }) => {
    const modal = useModal();
    if (!modal.visible) {
      return null;
    }
    return (
      <motion.div
        className="fixed inset-0 flex items-center justify-center bg-black/70 z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={() => modal.hide().then(modal.remove)}
      >
        <motion.div
          className="bg-[#0d2b45] border-2 border-[#d08159] p-6 max-w-md text-center relative font-pixel"
          initial={{ scale: 0.8, y: 50, opacity: 0 }}
          animate={{
            scale: 1,
            y: 0,
            opacity: 1,
            transition: {
              type: "spring",
              stiffness: 300,
              damping: 15,
            },
          }}
          exit={{
            scale: 0.9,
            y: 20,
            opacity: 0,
            transition: { duration: 0.3 },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <motion.h2
            className="text-xl mb-4 relative"
            initial={{ x: -20 }}
            animate={{ x: 0 }}
            transition={{ delay: 0.3, type: "spring" }}
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 1, 0, 1] }}
              transition={{ duration: 1.5, delay: 0.5 }}
              className="absolute -left-4 text-[#d08159]"
            >
              &gt;
            </motion.span>
            {showNothingText
              ? "█▓▒░ 你已涉嫌浪费计算资源 ░▒▓█"
              : "█▓▒░ 检测到时空锚点 ░▒▓█"}
          </motion.h2>
          <motion.p
            className="mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            {showNothingText
              ? "已派出武装直升机前往你的坐标"
              : "截图并分享可以获得“没有东西”哦~"}
          </motion.p>
          <motion.button
            className="px-4 py-2 bg-[#d08159] text-[#0d2b45] hover:bg-[#ffaa5e] transition-colors"
            onClick={() => modal.hide().then(modal.remove)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            {showNothingText ? "ᓖ모" : "?"}
          </motion.button>
          <motion.div
            className="absolute -inset-[1px] -z-10 border border-[#d08159] opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 1 }}
          />
          <motion.div
            className="absolute -inset-[3px] -z-20 border border-[#d08159] opacity-20"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.2 }}
            transition={{ delay: 1.2 }}
          />
        </motion.div>
      </motion.div>
    );
  },
);
